// GalleryAssetLayout.tsx
import React, { useMemo } from "react";
import styled from "styled-components";
import { Box, Flex, AssetType, Item, useThemeColor } from "@thenounproject/lingo-core";

import ColorDisplay from "../ColorDisplay";
import FontSample from "../FontSample";
import FontAssetSquare from "./GalleryAssetSquares/FontAssetSquare";
import ImageAssetSquare from "./GalleryAssetSquares/ImageAssetSquare";
import ColorThumbnail from "./GalleryAssetThumbnail/ColorThumbnail";
import ImageThumbnail from "./GalleryAssetThumbnail/ImageThumbnail";
import LinkMeta from "./GalleryAssetMeta/LinkMeta";
import DefaultMeta from "./GalleryAssetMeta/DefaultMeta";

type Layout = "square" | "full";

type Props = {
  item: Item;
  layout: Layout;
  thumbnailWidth?: string;
  children?: React.ReactNode;
  overflowButton?: React.ReactNode;
  useButton?: React.ReactNode;
};

const LayoutWrapper = styled(Flex)`
  width: 100%;
  border-radius: 4px;
  overflow: visible;
  position: relative;
`;

const ThumbnailContainer = styled(Box)<{ layout: Layout; width?: string }>`
  position: relative;
  flex-shrink: 0;
  min-width: 65px;
  ${({ layout, width }) =>
    layout === "square"
      ? `
        width: 100%;
      `
      : `
        width: ${width || "80px"};
      `}
`;

const ThumbnailInner = styled(Flex).attrs({
  width: "100%",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "default",
  overflow: "hidden",
})<{ previewShouldHugImage?: boolean }>`
  aspect-ratio: 1;

  ${({ previewShouldHugImage }) =>
    previewShouldHugImage &&
    `
    aspect-ratio: unset;

    & img {
      position: static;
      transform: none;
      max-width: 100%;
      height: auto;
      display: block;
      margin: 0 auto;
    }
  `}
`;

const TopRightOverlay = styled(Flex)`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 10;
  gap: 4px;
`;

const BottomRightOverlay = styled(Flex)`
  position: absolute;
  bottom: 4px;
  right: 4px;
  z-index: 10;
  gap: 4px;
`;

type ContentContainerProps = {
  hasBackground?: boolean;
  imageAlignment: Item["displayProperties"]["imageAlignment"];
  isSquare?: boolean;
};
const ContentContainer = styled(Flex).attrs<ContentContainerProps>(props => {
  return {
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    justifyContent: "flex-start",
    pl:
      !props.isSquare && props.imageAlignment === "trailing" && props.hasBackground
        ? "16px"
        : "0px",
    pr:
      !props.isSquare && props.imageAlignment === "leading" && props.hasBackground ? "16px" : "0px",
    py: !props.isSquare && props.imageAlignment !== "top" && props.hasBackground ? "16px" : "0px",
    width: props.isSquare ? "100%" : "auto",
  };
})<ContentContainerProps>``;

const GalleryAssetLayout: React.FC<Props> = ({
  item,
  layout,
  thumbnailWidth,
  children,
  overflowButton,
  useButton,
}) => {
  const { asset } = item;
  const isSquare = layout === "square";

  const previewShouldHugImage =
    (layout === "full" || item.displayProperties?.size === 1) &&
    (Boolean(asset.meta?.preview?.dimensions) || asset.dimensions !== "0x0");

  const inspectable = useMemo(() => ({ item, asset }), [item, asset]);
  const hasCardBackground = item.displayProperties?.cardBackgroundColor !== "clear";
  const cardBackground = useThemeColor(item.displayProperties?.cardBackgroundColor);
  const imageAlignment = item.displayProperties?.imageAlignment;
  const showMetadata = item.displayProperties?.showMetadata ?? true;

  const flexDirection = useMemo(() => {
    if (isSquare) return "column";
    return imageAlignment === "trailing" ? "row-reverse" : "row";
  }, [isSquare, imageAlignment]);

  const thumbnail = useMemo(() => {
    if (isSquare) {
      switch (asset?.type) {
        case AssetType.color:
          return <ColorDisplay asset={asset} />;
        case AssetType.textStyle:
          return <FontAssetSquare asset={asset} />;
        default:
          return <ImageAssetSquare inspectable={inspectable} />;
      }
    } else {
      switch (asset?.type) {
        case AssetType.textStyle:
          return <FontSample asset={asset} fontSize="64px" />;
        case AssetType.color:
          return <ColorThumbnail asset={asset} />;
        default:
          return <ImageThumbnail asset={asset} item={item} />;
      }
    }
  }, [asset, item, isSquare, inspectable]);

  const metadata = useMemo(() => {
    if (!showMetadata) return null;
    switch (asset?.type) {
      case AssetType.URL:
        return <LinkMeta asset={asset} background={cardBackground} />;
      default:
        return <DefaultMeta asset={asset} background={cardBackground} />;
    }
  }, [asset, showMetadata, cardBackground]);

  const renderButtons = () => {
    if (!overflowButton && !useButton) return null;

    return (
      <>
        <TopRightOverlay>{overflowButton}</TopRightOverlay>
        <BottomRightOverlay>{useButton}</BottomRightOverlay>
      </>
    );
  };

  return (
    <LayoutWrapper
      background={cardBackground}
      p={hasCardBackground ? "8px" : "0px"}
      flexDirection={flexDirection}
      alignItems="center"
      gap={isSquare ? "0" : "24px"}
      data-testid="gallery-asset-layout">
      {!isSquare && renderButtons()}

      <ThumbnailContainer layout={layout} width={thumbnailWidth}>
        <ThumbnailInner previewShouldHugImage={previewShouldHugImage}>{thumbnail}</ThumbnailInner>
        {isSquare && renderButtons()}
      </ThumbnailContainer>

      <ContentContainer
        hasBackground={hasCardBackground}
        imageAlignment={imageAlignment}
        isSquare={isSquare}>
        {children}
        {metadata}
      </ContentContainer>
    </LayoutWrapper>
  );
};

export default React.memo(GalleryAssetLayout);
